import React from 'react';
import axios from 'helpers/axios';
import config from 'helpers/config';
import FormModes from 'helpers/FormModes';
import { Calendar, Save, Edit, X, Info, AlertCircle, BanIcon, UserX } from 'lucide-react';

interface Props {}

interface State {
    settings: {
        enableNoShowRestriction: boolean;
        noShowThreshold: number;
        restrictionDurationDays: number;
        allowManualOverride: boolean;
    };
    formState: FormModes;
    isLoading: boolean;
    beforeEdit: any;
}

class AppointmentNoShowSettings extends React.Component<Props, State> {
    constructor(props: Props) {
        super(props);

        this.state = {
            settings: {
                enableNoShowRestriction: false,
                noShowThreshold: 3,
                restrictionDurationDays: 30,
                allowManualOverride: true,
            },
            formState: FormModes.Preview,
            isLoading: false,
            beforeEdit: {},
        };

        this.handleChange = this.handleChange.bind(this);
        this.handleToggle = this.handleToggle.bind(this);
    }

    componentDidMount() {
        this.getSettings();
    }

    getSettings() {
        axios
            .get(`${config.apiUrl}/tenant/settings`)
            .then((res) => {
                const tenantSettings = res.data.tenantSettings;
                if (tenantSettings) {
                    this.setState({
                        settings: {
                            enableNoShowRestriction: tenantSettings.enableNoShowRestriction || false,
                            noShowThreshold: tenantSettings.noShowThreshold || 3,
                            restrictionDurationDays: tenantSettings.restrictionDurationDays || 30,
                            allowManualOverride: tenantSettings.allowManualOverride !== undefined ? tenantSettings.allowManualOverride : true,
                        },
                    });
                }
            })
            .catch((error) => {
                console.log('Settings not found, using defaults', error);
            });
    }

    handleChange(event: React.FormEvent<HTMLInputElement>) {
        const { name, value } = event.currentTarget;
        const numValue = parseInt(value, 10);

        this.setState({
            settings: {
                ...this.state.settings,
                [name]: numValue > 0 ? numValue : 1,
            },
        });
    }

    handleToggle(name: string) {
        this.setState({
            settings: {
                ...this.state.settings,
                // @ts-ignore
                [name]: !this.state.settings[name as keyof typeof this.state.settings],
            },
        });
    }

    handleUpdate() {
        const { settings } = this.state;
        this.setState({ isLoading: true });
        axios
            .put(`${config.apiUrl}/tenant`, settings)
            .then(() => {
                this.setState({ isLoading: false, formState: FormModes.Preview });
            })
            .catch((error) => {
                console.error('Error updating settings:', error);
                this.setState({ isLoading: false });
            });
    }

    render() {
        const { settings, formState, isLoading } = this.state;

        return (
            <div className="flex flex-col min-h-screen bg-gray-50">
                {/* Header */}
                <div className="bg-white border-b border-gray-200 px-8 py-6">
                    <div className="flex items-center justify-between">
                        <div>
                            <nav className="flex items-center space-x-2 text-sm text-gray-500 mb-2">
                                <span>Settings</span>
                                <span>/</span>
                                <span className="text-blue-600 font-semibold">Appointment No-Show Rules</span>
                            </nav>
                            <h2 className="text-3xl font-bold text-gray-900">Appointment No-Show Rules</h2>
                            <p className="text-gray-600 mt-1">
                                Configure restrictions for customers with repeated no-show appointments
                            </p>
                        </div>
                        <div className="flex items-center space-x-3">
                            {formState === FormModes.Preview && (
                                <button
                                    onClick={() => {
                                        this.setState({ formState: FormModes.Edit, beforeEdit: this.state });
                                    }}
                                    className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-semibold flex items-center space-x-2 transition-colors"
                                >
                                    <Edit className="h-4 w-4" />
                                    <span>Edit Settings</span>
                                </button>
                            )}

                            {formState === FormModes.Edit && (
                                <div className="flex space-x-3">
                                    <button
                                        onClick={() => {
                                            this.setState({ ...this.state.beforeEdit, beforeEdit: {} });
                                        }}
                                        disabled={isLoading}
                                        className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-lg font-semibold flex items-center space-x-2 transition-colors disabled:opacity-50"
                                    >
                                        <X className="h-4 w-4" />
                                        <span>Cancel</span>
                                    </button>
                                    <button
                                        onClick={() => {
                                            this.handleUpdate();
                                        }}
                                        disabled={isLoading}
                                        className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg font-semibold flex items-center space-x-2 transition-colors disabled:opacity-50"
                                    >
                                        <Save className="h-4 w-4" />
                                        <span>{isLoading ? 'Saving...' : 'Save Changes'}</span>
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                <div className="flex-1 p-8">
                    {/* Status Banner */}
                    <div className="mb-8">
                        <div
                            className={`rounded-lg border p-4 ${
                                formState === FormModes.Edit
                                    ? 'bg-blue-50 border-blue-200'
                                    : 'bg-gray-50 border-gray-200'
                            }`}
                        >
                            <div className="flex items-center">
                                <div
                                    className={`p-2 rounded-lg ${
                                        formState === FormModes.Edit ? 'bg-blue-100' : 'bg-gray-100'
                                    }`}
                                >
                                    <UserX
                                        className={`h-5 w-5 ${
                                            formState === FormModes.Edit ? 'text-blue-600' : 'text-gray-600'
                                        }`}
                                    />
                                </div>
                                <div className="ml-4">
                                    <h3
                                        className={`text-lg font-semibold ${
                                            formState === FormModes.Edit ? 'text-blue-900' : 'text-gray-900'
                                        }`}
                                    >
                                        {formState === FormModes.Edit
                                            ? 'Editing No-Show Restriction Rules'
                                            : 'No-Show Restriction Rules Overview'}
                                    </h3>
                                    <p
                                        className={`text-sm ${
                                            formState === FormModes.Edit ? 'text-blue-700' : 'text-gray-600'
                                        }`}
                                    >
                                        {formState === FormModes.Edit
                                            ? 'Make your changes below and click Save Changes to apply them.'
                                            : settings.enableNoShowRestriction
                                            ? 'No-show restrictions are currently enabled'
                                            : 'No-show restrictions are currently disabled'}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Main Settings Section */}
                    <div className="space-y-8">
                        {/* Enable/Disable Section */}
                        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                            <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
                                <div className="flex items-center">
                                    <BanIcon className="h-5 w-5 text-gray-500 mr-3" />
                                    <h3 className="text-lg font-semibold text-gray-900">
                                        Restriction Status
                                    </h3>
                                </div>
                            </div>

                            <div className="p-6">
                                <div className="flex items-center justify-between">
                                    <div className="flex-1">
                                        <label className="text-sm font-medium text-gray-700 block mb-1">
                                            Enable No-Show Restrictions
                                        </label>
                                        <p className="text-sm text-gray-500">
                                            When enabled, customers with repeated no-shows will be restricted from
                                            booking new appointments
                                        </p>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => this.handleToggle('enableNoShowRestriction')}
                                        disabled={formState === FormModes.Preview}
                                        className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                                            settings.enableNoShowRestriction ? 'bg-blue-600' : 'bg-gray-200'
                                        } ${formState === FormModes.Preview ? 'opacity-50 cursor-not-allowed' : ''}`}
                                    >
                                        <span
                                            className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                                                settings.enableNoShowRestriction ? 'translate-x-5' : 'translate-x-0'
                                            }`}
                                        />
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* Configuration Section */}
                        <div
                            className={`bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden transition-opacity ${
                                !settings.enableNoShowRestriction ? 'opacity-50' : ''
                            }`}
                        >
                            <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
                                <div className="flex items-center">
                                    <AlertCircle className="h-5 w-5 text-gray-500 mr-3" />
                                    <h3 className="text-lg font-semibold text-gray-900">Restriction Rules</h3>
                                </div>
                            </div>

                            <div className="p-6">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    {/* No-Show Threshold */}
                                    <div className="space-y-2">
                                        <label className="flex items-center text-sm font-medium text-gray-700">
                                            <UserX className="h-4 w-4 text-gray-400 mr-2" />
                                            No-Show Threshold
                                            <span className="text-red-500 ml-1">*</span>
                                        </label>
                                        <input
                                            type="number"
                                            name="noShowThreshold"
                                            min="1"
                                            max="99"
                                            value={settings.noShowThreshold}
                                            onChange={this.handleChange}
                                            disabled={
                                                formState === FormModes.Preview || !settings.enableNoShowRestriction
                                            }
                                            className={`w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${
                                                formState === FormModes.Preview || !settings.enableNoShowRestriction
                                                    ? 'bg-gray-50 border-gray-200 text-gray-600 cursor-not-allowed'
                                                    : 'border-gray-300 hover:border-gray-400'
                                            }`}
                                        />
                                        <p className="text-xs text-gray-500">
                                            Number of no-shows before restriction is applied
                                        </p>
                                    </div>

                                    {/* Restriction Duration */}
                                    <div className="space-y-2">
                                        <label className="flex items-center text-sm font-medium text-gray-700">
                                            <Calendar className="h-4 w-4 text-gray-400 mr-2" />
                                            Restriction Duration (Days)
                                            <span className="text-red-500 ml-1">*</span>
                                        </label>
                                        <input
                                            type="number"
                                            name="restrictionDurationDays"
                                            min="1"
                                            max="365"
                                            value={settings.restrictionDurationDays}
                                            onChange={this.handleChange}
                                            disabled={
                                                formState === FormModes.Preview || !settings.enableNoShowRestriction
                                            }
                                            className={`w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${
                                                formState === FormModes.Preview || !settings.enableNoShowRestriction
                                                    ? 'bg-gray-50 border-gray-200 text-gray-600 cursor-not-allowed'
                                                    : 'border-gray-300 hover:border-gray-400'
                                            }`}
                                        />
                                        <p className="text-xs text-gray-500">
                                            How long the restriction remains active (0 for permanent)
                                        </p>
                                    </div>
                                </div>

                                {/* Manual Override Toggle */}
                                <div className="mt-6 pt-6 border-t border-gray-200">
                                    <div className="flex items-center justify-between">
                                        <div className="flex-1">
                                            <label className="text-sm font-medium text-gray-700 block mb-1">
                                                Allow Manual Override
                                            </label>
                                            <p className="text-sm text-gray-500">
                                                Allow staff to manually override the restriction and book appointments
                                                for restricted customers
                                            </p>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => this.handleToggle('allowManualOverride')}
                                            disabled={
                                                formState === FormModes.Preview || !settings.enableNoShowRestriction
                                            }
                                            className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                                                settings.allowManualOverride ? 'bg-blue-600' : 'bg-gray-200'
                                            } ${
                                                formState === FormModes.Preview || !settings.enableNoShowRestriction
                                                    ? 'opacity-50 cursor-not-allowed'
                                                    : ''
                                            }`}
                                        >
                                            <span
                                                className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                                                    settings.allowManualOverride ? 'translate-x-5' : 'translate-x-0'
                                                }`}
                                            />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Current Configuration Summary */}
                        {settings.enableNoShowRestriction && (
                            <div className="bg-amber-50 border border-amber-200 rounded-lg p-6">
                                <div className="flex gap-4">
                                    <AlertCircle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" />
                                    <div>
                                        <h4 className="font-semibold mb-2 text-lg text-amber-900">
                                            Current Configuration
                                        </h4>
                                        <p className="text-sm text-amber-800">
                                            Customers will be restricted from booking new appointments after{' '}
                                            <span className="font-semibold">{settings.noShowThreshold}</span> no-show
                                            incidents. The restriction will last for{' '}
                                            <span className="font-semibold">
                                                {settings.restrictionDurationDays === 0
                                                    ? 'indefinitely'
                                                    : `${settings.restrictionDurationDays} days`}
                                            </span>
                                            .{' '}
                                            {settings.allowManualOverride
                                                ? 'Staff members will be able to manually override this restriction.'
                                                : 'No manual overrides will be allowed.'}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Info Panel */}
                    <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-6">
                        <div className="flex gap-4">
                            <Info className="w-6 h-6 text-blue-600 flex-shrink-0 mt-0.5" />
                            <div className="text-sm text-blue-800">
                                <h4 className="font-semibold mb-3 text-lg">How No-Show Restrictions Work</h4>
                                <ul className="space-y-2 text-blue-700">
                                    <li className="flex items-start gap-2">
                                        <span className="w-1.5 h-1.5 bg-blue-600 rounded-full mt-2 flex-shrink-0"></span>
                                        When a customer fails to show up for a scheduled appointment, it counts as a
                                        no-show incident
                                    </li>
                                    <li className="flex items-start gap-2">
                                        <span className="w-1.5 h-1.5 bg-blue-600 rounded-full mt-2 flex-shrink-0"></span>
                                        Once the threshold is reached, the system will automatically prevent that customer
                                        from booking new appointments
                                    </li>
                                    <li className="flex items-start gap-2">
                                        <span className="w-1.5 h-1.5 bg-blue-600 rounded-full mt-2 flex-shrink-0"></span>
                                        The restriction duration can be set to automatically expire after a specified
                                        number of days
                                    </li>
                                    <li className="flex items-start gap-2">
                                        <span className="w-1.5 h-1.5 bg-blue-600 rounded-full mt-2 flex-shrink-0"></span>
                                        Staff can be given permission to override restrictions for special cases when
                                        manual override is enabled
                                    </li>
                                    <li className="flex items-start gap-2">
                                        <span className="w-1.5 h-1.5 bg-blue-600 rounded-full mt-2 flex-shrink-0"></span>
                                        Customers will see a message explaining why they cannot book appointments if they
                                        are restricted
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }
}

export default AppointmentNoShowSettings;
